// "Download original PDF" — the file a paper was read from.
//
// The API checks who may have it and answers with a five-minute presigned
// link; the browser fetches the file from S3 directly (AGENTS.md rule 7). The
// link carries `Content-Disposition: attachment` with the uploaded name, so it
// saves as "Term 1 Maths.pdf" rather than opening a uuid. It is asked for on
// the click — never prefetched — so an expired link is never what is clicked.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiErrorMessage, apiGet } from '@/lib/api';

export function OriginalPdfButton({
  customPaperId,
  className,
  label = 'Download original PDF',
}: {
  customPaperId: string;
  className?: string;
  label?: string;
}) {
  const [error, setError] = useState('');
  const download = useMutation({
    mutationFn: () =>
      apiGet<{ url: string; fileName: string | null }>(
        `/teacher/custom-papers/${customPaperId}/original`,
      ),
    onSuccess: ({ url }) => {
      setError('');
      // An anchor rather than a navigation: if the file were ever served
      // inline, the console stays where it is.
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  return (
    <span className="inline-flex flex-col items-end">
      <button
        type="button"
        onClick={() => download.mutate()}
        disabled={download.isPending}
        className={
          className ?? 'text-slate-600 hover:text-forest hover:underline disabled:opacity-50'
        }
      >
        {download.isPending ? 'Preparing…' : label}
      </button>
      {error && <span className="mt-0.5 text-fs-11 font-medium text-red-700">{error}</span>}
    </span>
  );
}
